import {
  FluentProvider,
  makeStyles,
  Nav,
  NavCategory,
  NavCategoryItem,
  NavItem,
  NavSubItem,
  NavSubItemGroup,
  tokens,
  webLightTheme,
} from '@fluentui/react-components';
import { INavLink, INavLinkGroup } from '@m365-admin/nav';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { MOBILE_NAV_MAX_WIDTH_BREAKPOINT } from '../../../app/common/constants/SiteConstants';
import { bodyContentContainer } from '../../../app/common/styles/CommonStyleObjects';
import { getLinkValue, getSelectedNavValues } from '../../../utils/navUtils';

const useStyles = makeStyles({
  mobileView: {
    display: 'flex',
    flexDirection: 'row',
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      flexDirection: 'column',
    },
  },
  navSection: {
    width: '320px',
    minWidth: '320px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  navWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: tokens.spacingHorizontalMNudge,
    paddingRight: tokens.spacingHorizontalXS,
    rowGap: tokens.spacingVerticalXXS,
    // Ensure nav controls meet minimum target-size requirements.
    '& .fui-NavItem, & .fui-NavSubItem, & .fui-NavCategoryItem': {
      minHeight: '24px',
    },
  },
  detailsSection: {
    flexGrow: 1,
    minWidth: '0',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  childrenWrapper: {
    width: '100%',
    padding: '30px',
    overflow: 'auto',
    boxSizing: 'border-box',
  },
});

interface IMainDetailsViewProps {
  navLinkGroups: INavLinkGroup[];
  children: JSX.Element;
  navSectionHeader?: JSX.Element;
  commandBarSection?: JSX.Element;
  navAriaLabel?: string;
}

const MainDetailsView = ({
  navLinkGroups,
  children,
  navSectionHeader,
  commandBarSection,
  navAriaLabel = 'Side navigation',
}: IMainDetailsViewProps): JSX.Element => {
  const location = useLocation();
  const styles = useStyles();

  const { selectedValue, selectedCategoryValue } = getSelectedNavValues(
    navLinkGroups,
    location.pathname,
  );

  const renderNavLink = (link: INavLink, index: number, parentKey?: string): JSX.Element => {
    const value = getLinkValue(link, index, parentKey || 'link');
    const href = link.href || link.url;

    if (link.links?.length) {
      return (
        <NavCategory key={value} value={value}>
          <NavCategoryItem>{link.name}</NavCategoryItem>
          <NavSubItemGroup>
            {link.links.map((childLink, childIndex) => {
              const childValue =
                childLink.key || childLink.url || childLink.href || `${value}-child-${childIndex}`;
              return (
                <NavSubItem
                  key={childValue}
                  value={childValue}
                  href={childLink.href || childLink.url}
                  onClick={(ev: React.MouseEvent<HTMLElement>) => {
                    childLink.onClick?.(ev, childLink);
                  }}
                >
                  {childLink.name}
                </NavSubItem>
              );
            })}
          </NavSubItemGroup>
        </NavCategory>
      );
    }

    return (
      <NavItem
        key={value}
        value={value}
        href={href}
        onClick={(ev: React.MouseEvent<HTMLElement>) => {
          link.onClick?.(ev, link);
        }}
      >
        {link.name}
      </NavItem>
    );
  };

  const hasLinks = navLinkGroups.some((group) => (group.links || []).length > 0);

  return (
    <div className={`${bodyContentContainer}  ms-depth-4`}>
      <div className={styles.mobileView}>
        <div className={styles.navSection}>
          {navSectionHeader}
          <FluentProvider theme={webLightTheme}>
            {hasLinks && selectedValue !== undefined && (
              <Nav
                aria-label={navAriaLabel}
                selectedValue={selectedValue}
                selectedCategoryValue={selectedCategoryValue}
                className={styles.navWrapper}
              >
                {navLinkGroups.map((group) =>
                  (group.links || []).map((link, index) => renderNavLink(link, index, group.key)),
                )}
              </Nav>
            )}
          </FluentProvider>
        </div>
        <div className={styles.detailsSection}>
          {commandBarSection}
          <div className={styles.childrenWrapper}>{children}</div>
        </div>
      </div>
    </div>
  );
};
export default MainDetailsView;
